import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DraggableRichText from './DraggableRichText';
import { RootState } from '../../../redux';
import {
  updateRichTextElement,
  updateRichTextPosition,
  updateRichTextSize,
  removeRichTextElement,
  setSelectedRichTextId,
  setSelectedImageId,
} from '../../../redux/TabTile/TabActions';
import { Editor } from 'slate';
import { RichTextElement } from '../../../redux/TabTile/TabStateInterfaces';

interface RichTextProps {
  softUI: boolean;
  colorScheme?: string;
  sidebarOpen?: boolean;
  dashMode: string;
  currentTabId: number;
  setSidebarOpen?: (open: boolean) => void;
  showObjectsSidebar?: boolean;
  setShowObjectsSidebar?: (show: boolean) => void;
  setShowListofTileMenu?: (show: boolean) => void;
  setLayerMode?: React.Dispatch<React.SetStateAction<'tiles' | 'objects'>>;
  setDashboardResizeColumn?: (resize: boolean) => void;
  setSelectedObject?: (obj: string | null) => void;
  dashboardHeight?: number;
  setShowDashboardFilterEdit?: (show: boolean) => void;
  dashboardWidth?: number;
  setActiveEditor: (editor: Editor | null) => void;
  onEditorChange?: () => void;
  isSelected?: boolean;
  color?: string;
  backgroundColor?: string;
}

const RichText: React.FC<RichTextProps> = ({
  softUI,
  color,
  colorScheme,
  dashMode,
  currentTabId,
  setSelectedObject,
  dashboardHeight,
  dashboardWidth,
  isSelected,
  backgroundColor,
  setActiveEditor,
  onEditorChange,
  setShowListofTileMenu,
  setLayerMode,
  setDashboardResizeColumn,
  setShowObjectsSidebar,
  setSidebarOpen,
  setShowDashboardFilterEdit,
}) => {
  const dispatch = useDispatch();

  const { richTextElements, richTextElementId } = useSelector((state: RootState) => {
    const tabData = state.tabState.tabs[currentTabId]?.dashboardObjects || {};
    return {
      richTextElements: tabData.richTextElements || [],
      richTextElementId: tabData.richTextElementId || null,
    };
  });

  const handleDragStop = useCallback(
    (id: string, x: number, y: number) => {
      dispatch(updateRichTextPosition(currentTabId, id, x, y));
    },
    [dispatch, currentTabId],
  );

  const handleResizeStop = useCallback(
    (id: string, width: number, height: number, x: number, y: number) => {
      dispatch(updateRichTextSize(currentTabId, id, width, height, x, y));
    },
    [dispatch, currentTabId],
  );

  const handleDelete = useCallback(
    (id: string) => {
      dispatch(removeRichTextElement(currentTabId, id));
      if (richTextElementId === id) {
        dispatch(setSelectedRichTextId(currentTabId, null));
        setSelectedObject?.(null);
      }
    },
    [dispatch, currentTabId, richTextElementId, setSelectedObject],
  );

  const handleClick = (el: string) => {
    if (richTextElementId === el) return;
    dispatch(setSelectedImageId(currentTabId, null));
    dispatch(setSelectedRichTextId(currentTabId, el));
    setSidebarOpen?.(true);
    setSelectedObject?.('text');
    setShowObjectsSidebar?.(false);
    setShowListofTileMenu?.(true);
    setLayerMode?.('objects');
    setDashboardResizeColumn?.(false);
    setShowDashboardFilterEdit?.(false);
  };

  if (richTextElements.length === 0) {
    return null;
  }

  return (
    <>
      {richTextElements.map((element: RichTextElement) => (
        <DraggableRichText
          key={element.id}
          id={element.id}
          element={element}
          onDragStop={handleDragStop}
          onResizeStop={handleResizeStop}
          onDelete={handleDelete}
          onClick={() => handleClick(element.id)}
          softUI={softUI}
          colorScheme={colorScheme}
          color={color}
          backgroundColor={backgroundColor}
          dashMode={dashMode}
          dashboardHeight={dashboardHeight}
          isSelected={richTextElementId === element.id}
          setActiveEditor={setActiveEditor}
          onEditorChange={onEditorChange}
          dashboardWidth={dashboardWidth}
        />
      ))}
    </>
  );
};

export default RichText;
